/**
 * Vendor Portal Module
 * External vendor access to Works Management System
 */

import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import type { Module } from '@/modules/index';
import { ThemeProvider } from './context/ThemeContext';
import { isSessionValid, rememberPostLoginRedirect } from './services/vendorService';
import type { ThemeId } from './lib/types';
import { PageSkeleton } from '@/components/base/Skeleton';

// Lazy load pages
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ContractorLayout = lazy(() => import('./components/layout/ContractorLayout'));
const WorksList = lazy(() => import('./pages/works/WorksList'));
const WorkDetails = lazy(() => import('./pages/works/WorkDetails'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const ProgressPage = lazy(() => import('./pages/progress/ProgressPage'));
const SchedulePage = lazy(() => import('./pages/schedule/SchedulePage'));
const FinancialPage = lazy(() => import('./pages/financial/FinancialPage'));
const SubUsersPage = lazy(() => import('./pages/users/SubUsersPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const DisplayDevicesPage = lazy(() => import('./pages/display-devices/DisplayDevicesPage'));
const ApplyLicence = lazy(() => import('./pages/display-devices/ApplyLicence'));
const ReceiptsPage = lazy(() => import('./pages/receipts/ReceiptsPage'));

// Get initial theme from stored user
const getInitialTheme = (): ThemeId => {
  try {
    const userData = localStorage.getItem('vendor_user');
    if (userData) {
      const user = JSON.parse(userData);
      return (user.thm_id || 1) as ThemeId;
    }
  } catch (e) {
    // Ignore parse errors
  }
  return 1;
};

// Loading fallback
const LoadingFallback = () => (
  <PageSkeleton />
);

// Auth wrapper component with theme provider
const ProtectedLayout = () => {
  // Checking the token's expiry (not merely its presence) stops the portal
  // from rendering a live-looking page over a dead session — which is what let
  // a vendor click Device Permission and only then get bounced to login.
  if (!isSessionValid()) {
    rememberPostLoginRedirect();
    return <Navigate to="/app/vendor-portal/login" replace />;
  }
  return (
    <ThemeProvider initialThemeId={getInitialTheme()}>
      <Suspense fallback={<LoadingFallback />}>
        <ContractorLayout />
      </Suspense>
    </ThemeProvider>
  );
};

const dsVendorModule: Module = {
  name: 'ds-vendor',
  path: '/app/vendor-portal',
  routes: [
    // Login route (public)
    {
      path: '/app/vendor-portal/login',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <Login />
        </Suspense>
      )
    },
    // Register route (public)
    {
      path: '/app/vendor-portal/register',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <Register />
        </Suspense>
      )
    },
    // Forgot-password route (public)
    {
      path: '/app/vendor-portal/forgot-password',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <ForgotPassword />
        </Suspense>
      )
    },
    // Protected routes with layout
    {
      path: '/app/vendor-portal',
      element: <ProtectedLayout />,
      children: [
        // Display Devices is the portal's only module, so it *is* the landing
        // screen — there is no dashboard to pass through.
        {
          index: true,
          element: <Navigate to="display-devices" replace />
        },
        // Old bookmarks / stored post-login redirects still point at
        // /dashboard; forward them instead of 404-ing.
        {
          path: 'dashboard',
          element: <Navigate to="/app/vendor-portal/display-devices" replace />
        },
        {
          path: 'works',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WorksList />
            </Suspense>
          )
        },
        {
          path: 'works/:contractId',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WorkDetails />
            </Suspense>
          )
        },
        {
          path: 'documents',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <DocumentsPage />
            </Suspense>
          )
        },
        {
          path: 'progress',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProgressPage />
            </Suspense>
          )
        },
        {
          path: 'schedule',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SchedulePage />
            </Suspense>
          )
        },
        {
          path: 'financial',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <FinancialPage />
            </Suspense>
          )
        },
        {
          path: 'users',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SubUsersPage />
            </Suspense>
          )
        },
        {
          path: 'display-devices',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <DisplayDevicesPage />
            </Suspense>
          )
        },
        {
          path: 'display-devices/apply-licence',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ApplyLicence />
            </Suspense>
          )
        },
        {
          path: 'display-devices/receipts',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ReceiptsPage />
            </Suspense>
          )
        },
        {
          path: 'profile',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProfilePage />
            </Suspense>
          )
        }
      ]
    }
  ]
};

export default dsVendorModule;
